import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import {
  TrendingUp,
  Building2,
  Users,
  DollarSign,
  Briefcase,
  GitBranch,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
} from "lucide-react";
import { Link } from "react-router-dom";

const ExecutiveDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [branches, setBranches] = useState([]);
  const [recentDeals, setRecentDeals] = useState([]);
  const [propertyStats, setPropertyStats] = useState(null);

  useEffect(() => {
    fetchExecutiveData();
  }, []);

  const fetchExecutiveData = async () => {
    try {
      const [crmRes, branchesRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/crm/stats`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/branches`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/properties?limit=5`, { withCredentials: true }).catch(() => ({ data: { total: 0 } })),
      ]);

      // Process CRM stats
      const crmStats = crmRes.data || {};
      setStats({
        totalLeads: crmStats.leads?.total || 0,
        newLeads: crmStats.leads?.new || 0,
        totalDeals: crmStats.deals?.total || 0,
        wonDeals: crmStats.deals?.won || 0,
        totalRevenue: crmStats.deals?.won_value || 0,
        pendingDeals: crmStats.deals?.pending || 0,
        properties: crmStats.properties || 0,
      });

      setBranches(branchesRes.data || []);
      
      setPropertyStats({
        total: propertiesRes.data?.total || propertiesRes.data?.length || 0,
      });

      // Fetch recent deals
      try {
        const dealsRes = await axios.get(`${API}/admin/deals`, { withCredentials: true });
        const deals = (dealsRes.data || [])
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5);
        setRecentDeals(deals);
      } catch (e) {}
    } catch (error) {
      console.error("Failed to fetch executive data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `K${(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Executive Dashboard</h1>
        <p className="text-gray-500">Company-wide performance overview and key metrics</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-xs text-green-600 flex items-center">
              <ArrowUpRight className="w-3 h-3" />
            </span>
          </div>
          <p className="text-2xl font-bold">{formatCurrency(stats?.totalRevenue)}</p>
          <p className="text-sm text-gray-500">Total Revenue</p>
        </div>

        <div className="bg-white rounded-lg border p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Briefcase className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold">{stats?.totalDeals || 0}</p>
          <p className="text-sm text-gray-500">Total Deals</p>
          <p className="text-xs text-green-600">{stats?.wonDeals || 0} won</p>
        </div>

        <div className="bg-white rounded-lg border p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <p className="text-2xl font-bold">{stats?.totalLeads || 0}</p>
          <p className="text-sm text-gray-500">Total Leads</p>
          <p className="text-xs text-blue-600">{stats?.newLeads || 0} new</p>
        </div>

        <div className="bg-white rounded-lg border p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Building2 className="w-5 h-5 text-orange-600" />
            </div>
          </div>
          <p className="text-2xl font-bold">{propertyStats?.total || 0}</p>
          <p className="text-sm text-gray-500">Properties</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Branch Overview */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <GitBranch className="w-4 h-4" />
              Branch Overview
            </h2>
            <Link to="/dashboard/admin/branches" className="text-sm text-primary hover:underline">
              View All
            </Link>
          </div>
          <div className="divide-y">
            {branches.length > 0 ? (
              branches.slice(0, 5).map((branch) => (
                <div key={branch.branch_id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Building2 className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{branch.name}</p>
                      <p className="text-sm text-gray-500">{branch.city}</p>
                    </div>
                  </div>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded">{branch.code}</span>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <GitBranch className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No branches created yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Deals */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Recent Deals
            </h2>
            <Link to="/dashboard/admin/all-deals" className="text-sm text-primary hover:underline">
              View All
            </Link>
          </div>
          <div className="divide-y">
            {recentDeals.length > 0 ? (
              recentDeals.map((deal) => (
                <div key={deal.deal_id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <p className="font-medium">{deal.title}</p>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(deal.created_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-primary">{formatCurrency(deal.value)}</p>
                    <span className={`text-xs px-2 py-1 rounded ${
                      deal.status === "won" ? "bg-green-100 text-green-700" :
                      deal.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                      deal.status === "lost" ? "bg-red-100 text-red-700" :
                      "bg-gray-100 text-gray-600"
                    }`}>
                      {deal.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No deals yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="font-semibold mb-4">Quick Access</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/dashboard/admin/analytics" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-primary" />
            <p className="text-sm font-medium">Analytics</p>
          </Link>
          <Link to="/dashboard/admin/finance" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-center">
            <DollarSign className="w-6 h-6 mx-auto mb-2 text-primary" />
            <p className="text-sm font-medium">Finance</p>
          </Link>
          <Link to="/dashboard/admin/branches" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-center">
            <GitBranch className="w-6 h-6 mx-auto mb-2 text-primary" />
            <p className="text-sm font-medium">Organization</p>
          </Link>
          <Link to="/dashboard/admin/all-deals" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-center">
            <Briefcase className="w-6 h-6 mx-auto mb-2 text-primary" />
            <p className="text-sm font-medium">All Deals</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
